/* KarmaBadge — Luminous, animated karma display.
 *
 * Two variants:
 *   - compact: inline pill (used in chips/badges)
 *   - default: large glowing card with gradient progress bar, floating particles,
 *              rolling number, and aura that pulses when close to next level
 *
 * Designed to feel like a reward you WANT to grow. Visual incentive to keep
 * earning karma through missions, walks, map confirmations, companion logs.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Shield, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

/* Brand palette per level — matches backend _karma_level() labels */
const LEVEL_COLORS = {
  "Cittadino":       { start: "#B7AFA0", end: "#9A9080", glow: "rgba(154,144,128,0.45)" },
  "Soccorritore":    { start: "#2DBA60", end: "#48D478", glow: "rgba(45,186,96,0.55)" },
  "Guardiano":       { start: "#F4B83C", end: "#FFCF66", glow: "rgba(244,184,60,0.65)" },
  "Sentinella ORIO": { start: "#FF7A1A", end: "#FF9248", glow: "rgba(255,122,26,0.70)" },
  "Custode":         { start: "#A855F7", end: "#C77DFF", glow: "rgba(168,85,247,0.65)" },
};

function colorsFor(level) {
  return LEVEL_COLORS[level] || LEVEL_COLORS.Cittadino;
}

/* RollingNumber — smoothly counts up to target on mount/change */
function RollingNumber({ value, color }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const start = display;
    const target = value || 0;
    if (start === target) return;
    const duration = 800;
    const t0 = performance.now();
    let raf = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + (target - start) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <span className="font-display tabular-nums" style={{ color }}>
      {display.toLocaleString("it-IT")}
    </span>
  );
}

export default function KarmaBadge({ karma, compact = false }) {
  const balance = karma?.balance || 0;
  const level = karma?.level?.label || "Cittadino";
  const nextAt = karma?.level?.next_at;
  const c = colorsFor(level);

  /* Hook called BEFORE early returns to comply with rules-of-hooks */
  const particles = useMemo(
    () => Array.from({ length: 5 }, (_, i) => ({
      id: i,
      x: 12 + Math.random() * 70,
      delay: Math.random() * 2.5,
      duration: 3 + Math.random() * 2,
      size: 3 + Math.random() * 3,
    })),
    []
  );

  if (!karma) return null;

  /* compact pill used elsewhere */
  if (compact) {
    return (
      <div
        data-testid="karma-badge-compact"
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-mono"
        style={{
          background: `linear-gradient(135deg, ${c.start}22, ${c.end}11)`,
          color: c.start,
          border: `1px solid ${c.start}40`,
        }}
      >
        <Shield className="h-2.5 w-2.5" />
        <span className="font-semibold">{balance}</span>
        <span className="opacity-70">{level}</span>
      </div>
    );
  }

  const progress = nextAt ? Math.min(100, (balance / nextAt) * 100) : 100;
  const isMax = !nextAt;
  const isClose = !isMax && progress >= 75;
  const remaining = nextAt ? Math.max(0, nextAt - balance) : 0;

  return (
    <div
      data-testid="karma-badge"
      className="relative rounded-2xl p-4 sm:p-5 overflow-hidden border w-full min-w-0"
      style={{
        background: `linear-gradient(135deg, ${c.start}14 0%, ${c.end}08 60%, transparent 100%), #FFFFFF`,
        borderColor: `${c.start}33`,
        boxShadow: `0 12px 32px -16px ${c.glow}, 0 1px 4px -1px rgba(0,0,0,0.04)`,
      }}
    >
      {/* Aura glow */}
      <motion.div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0.55 }}
        animate={{ opacity: isClose ? [0.55, 0.95, 0.55] : [0.45, 0.65, 0.45] }}
        transition={{ duration: isClose ? 1.6 : 3.2, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background: `radial-gradient(60% 80% at 80% 50%, ${c.glow} 0%, transparent 60%)`,
        }}
      />

      {/* Floating sparkle particles */}
      {particles.map((p) => (
        <motion.div
          key={p.id}
          aria-hidden
          className="absolute pointer-events-none"
          initial={{ y: 0, opacity: 0 }}
          animate={{ y: -80, opacity: [0, 0.8, 0] }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeOut",
          }}
          style={{
            left: `${p.x}%`,
            bottom: 8,
            width: p.size,
            height: p.size,
            background: c.start,
            borderRadius: "50%",
            boxShadow: `0 0 ${p.size * 2}px ${c.start}`,
            filter: "blur(0.4px)",
          }}
        />
      ))}

      {/* HEADER: icon · label · balance */}
      <div className="relative z-10 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <motion.div
            className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: `linear-gradient(135deg, ${c.start}, ${c.end})`,
              color: "#FFFFFF",
              boxShadow: `0 6px 16px -6px ${c.glow}`,
            }}
            animate={{ rotate: isClose ? [0, -3, 3, -3, 0] : 0 }}
            transition={{ duration: 1.4, repeat: isClose ? Infinity : 0, repeatDelay: 1.6 }}
          >
            <Shield className="h-4 w-4" strokeWidth={2.4} />
          </motion.div>
          <div className="min-w-0">
            <div
              className="text-[9px] uppercase tracking-[0.22em] font-semibold leading-none"
              style={{ color: c.start }}
            >
              Karma ORIO
            </div>
            <div className="font-display text-[18px] sm:text-[20px] leading-tight mt-1 text-[#1A1A1A] truncate">
              {level}
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-display text-[26px] sm:text-[30px] leading-none">
            <RollingNumber value={balance} color={c.start} />
          </div>
          {karma.pending > 0 && (
            <div className="text-[10px] text-[#9A9A9A] mt-1">
              +{karma.pending} in attesa
            </div>
          )}
        </div>
      </div>

      {/* PROGRESS BAR — luminous gradient with shimmer */}
      {!isMax ? (
        <div className="relative z-10 mt-4">
          <div className="relative h-2 rounded-full bg-[#F0EBE3] overflow-hidden">
            <motion.div
              className="absolute inset-y-0 left-0 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1.1, ease: "easeOut" }}
              style={{
                background: `linear-gradient(90deg, ${c.start} 0%, ${c.end} 100%)`,
                boxShadow: `0 0 10px ${c.glow}, 0 0 20px ${c.glow}`,
              }}
            />
            <motion.div
              aria-hidden
              className="absolute inset-y-0 w-12 pointer-events-none"
              animate={{ x: ["-50%", "350%"] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "linear" }}
              style={{
                background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)",
                mixBlendMode: "screen",
              }}
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between text-[10.5px]">
            <span className="text-[#6B6B6B]">
              {remaining > 0 ? (
                <>Ancora <span className="font-semibold text-[#1A1A1A]">{remaining}</span> al prossimo livello</>
              ) : (
                "Quasi al prossimo livello"
              )}
            </span>
            <span style={{ color: c.start }} className="font-semibold tabular-nums">
              {Math.round(progress)}%
            </span>
          </div>
        </div>
      ) : (
        <div className="relative z-10 mt-4 flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: c.start }}>
          <Sparkles className="h-3.5 w-3.5" />
          Livello massimo raggiunto
        </div>
      )}
    </div>
  );
}
